import type { ColumnDef } from '@tanstack/react-table'
import type { ITenantDestination } from '@/entities/tenant-destination'
import type { PaginationConfigInput } from '@/shared/ui/data-table'
import { Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TenantDestinationStatusToggle } from '@/features/tenant-destination/tenant-destination-status-toggle'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { DataTable } from '@/shared/ui/data-table'

interface TenantDestinationListTableProps {
  data: ITenantDestination[]
  loading: boolean
  pagination: PaginationConfigInput
  onPageChange: (page: number, pageSize: number) => void
  onEdit: (item: ITenantDestination) => void
  onToggleStatus: (item: ITenantDestination, checked: boolean) => void
  onDelete: (item: ITenantDestination) => void
}

export function TenantDestinationListTable({
  data,
  loading,
  pagination,
  onPageChange,
  onEdit,
  onToggleStatus,
  onDelete,
}: TenantDestinationListTableProps) {
  const { t } = useTranslation()
  const columns = useMemo<ColumnDef<ITenantDestination>[]>(
    () => [
      {
        id: 'thumbnail',
        size: 96,
        header: () => <div className="text-center">{t('destination.thumbnail')}</div>,
        cell: ({ row }) => {
          const primary = [row.original.thumbnail, row.original.banner].find(Boolean) ?? ''
          const fallback =
            [row.original.fallback?.thumbnail, row.original.fallback?.banner].find(Boolean) ?? ''
          const src = primary || fallback
          return (
            <div className="flex justify-center">
              {src ? (
                <img
                  src={src}
                  alt=""
                  className="h-12 w-16 rounded-md object-cover ring-1 ring-zinc-200"
                  onError={(event) => {
                    const img = event.currentTarget
                    if (fallback && img.src !== fallback) {
                      img.src = fallback
                    }
                  }}
                />
              ) : (
                <div className="flex h-12 w-16 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-medium text-zinc-400 ring-1 ring-zinc-200">
                  N/A
                </div>
              )}
            </div>
          )
        },
      },
      {
        id: 'name',
        header: t('common.name'),
        cell: ({ row }) => (
          <span className="line-clamp-2 text-sm font-medium text-zinc-900">
            {Object.values(row.original.localizedData ?? {})[0]?.name ?? row.original.id}
          </span>
        ),
      },
      {
        id: 'type',
        size: 120,
        header: () => <div className="text-center">{t('common.type')}</div>,
        cell: ({ row }) => (
          <div className="flex justify-center">
            <Badge variant="outline">
              {row.original.destinationId
                ? t('tenantDestination.linked')
                : t('tenantDestination.custom')}
            </Badge>
          </div>
        ),
      },
      {
        id: 'status',
        size: 120,
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => (
          <div className="flex justify-center">
            <TenantDestinationStatusToggle
              checked={row.original.isActive}
              onCheckedChange={(checked) => onToggleStatus(row.original, checked)}
            />
          </div>
        ),
      },
      {
        id: 'actions',
        size: 64,
        header: () => null,
        cell: ({ row }) => (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:bg-red-50 hover:text-red-600"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(row.original)
              }}
            >
              <Trash2 size={15} />
            </Button>
          </div>
        ),
      },
    ],
    [onToggleStatus, onDelete, t],
  )
  const paginationConfig = useMemo(
    () => ({
      ...pagination,
      onChange: onPageChange,
    }),
    [onPageChange, pagination],
  )

  return (
    <DataTable
      columns={columns}
      data={data}
      loading={loading}
      emptyText={t('common.noData', { defaultValue: 'No data' })}
      pagination={paginationConfig}
      onRowClick={onEdit}
    />
  )
}
